import fromEntries from 'fromentries'
import { toSlug } from '../../../../lib/emneknagg'
/*
 * Handles valgte emneknagger and calculates masonry layout on mobile
 *
 * Layout for emneknagger.
 * Emneknagger are positioned with horisontal masonry on mobile devices, and with regular inline flow on desktop.
 *
 * Once support for masonry gains support in css this should be replaced with pure css.
 * */
export class Emneknagger extends HTMLElement {
  static get observedAttributes() {
    return ['data-valgte-emneknagger', 'data-alle-emneknagger']
  }

  currentLayout

  connectedCallback() {
    window.addEventListener('resize', this._layout)
    // eslint-disable-next-line compat/compat
    document.fonts?.ready?.then(this._layout)

    this._layout()
    for (const link of this.links) {
      link.addEventListener('click', this._handleLinkClick)
      link.addEventListener('keydown', this._handleLinkClick)

      /* Disable links */
      link.removeAttribute('href')
    }
    this._updateDisabledEmneknagger()
  }

  attributeChangedCallback(attributeName) {
    if (attributeName === 'data-valgte-emneknagger') {
      this._updateValgteEmneknagger()
    } else if (attributeName === 'data-alle-emneknagger') {
      this._updateDisabledEmneknagger()
    }
  }

  _layout = () => {
    if (document.documentElement.clientWidth >= 900) {
      this._layoutDesktop()
    } else {
      this._layoutMobile()
    }
  }

  _layoutDesktop() {
    if (this.currentLayout === 'desktop') {
      return
    }
    for (let item of this.items) {
      item.style.position = 'static'
    }
    this.currentLayout = 'desktop'
  }

  _layoutMobile() {
    const gutter = 7
    let row1 = 0
    let row2 = 0
    let rowHeight = 0
    let paddingTop = 0
    let width = 0
    for (let item of this.items) {
      item.style.position = 'absolute'
      const itemWidth = item.offsetWidth
      if (row1 <= row2) {
        item.style.top = `${paddingTop}px`
        item.style.left = `${row1}px`
        row1 += itemWidth + gutter

        if (rowHeight === 0) {
          rowHeight = item.offsetHeight
        }
      } else {
        item.style.top = `${paddingTop + rowHeight + gutter}px`
        item.style.left = `${row2}px`
        row2 += itemWidth + gutter
      }
      width = Math.max(row1, row2) - gutter
    }
    this.list.style.setProperty('--mobile-width', `${width}px`)
    this.currentLayout = 'mobile'
  }

  _handleLinkClick = (e) => {
    if (e.type === 'keydown' && e.keyCode !== 13 && e.keyCode !== 32) {
      // Ignore keydown from keys other than space and enter
      return
    }
    e.preventDefault()
    const link = e.target
    if (link.getAttribute('aria-disabled')) {
      return
    }
    const currentEmneknagg = link.getAttribute('data-emneknagg')
    if (this.valgteEmneknagger.includes(currentEmneknagg)) {
      this.valgteEmneknagger = this.valgteEmneknagger.filter(
        (emneknagg) => emneknagg !== currentEmneknagg
      )
    } else {
      this.valgteEmneknagger = [...this.valgteEmneknagger, currentEmneknagg]
    }

    let event
    if (typeof CustomEvent === 'function') {
      event = new CustomEvent('dh-sexguiden-emneknagger-updated', {
        bubbles: true,
        cancelable: true,
      })
    } else {
      event = document.createEvent('CustomEvent')
      event.initEvent('dh-sexguiden-emneknagger-updated', true, true)
    }
    this.dispatchEvent(event)
  }

  _updateValgteEmneknagger() {
    for (const link of this.links) {
      const emneknagg = link.getAttribute('data-emneknagg')
      const active = this.valgteEmneknagger.includes(emneknagg)
      link.setAttribute('aria-pressed', active.toString())
    }
  }

  _updateDisabledEmneknagger() {
    /* Create map for fast lookup */
    const emneknaggerMap = fromEntries(
      this.alleEmneknagger.map((emneknagg) => [toSlug(emneknagg), emneknagg])
    )

    for (const link of this.links) {
      const emneknagg = link.getAttribute('data-emneknagg')
      const active = this.valgteEmneknagger.includes(emneknagg)
      const disabled = !active && !emneknaggerMap[emneknagg]?.count
      if (disabled) {
        link.setAttribute('aria-disabled', 'true')
        link.removeAttribute('tabindex')
      } else {
        link.removeAttribute('aria-disabled')
        link.setAttribute('tabindex', '0')
      }
    }
  }

  get links() {
    return Array.from(this.querySelectorAll('a[data-emneknagg]'))
  }

  get list() {
    return this.querySelector('ul')
  }

  get items() {
    return Array.from(this.querySelectorAll('li'))
  }

  get valgteEmneknagger() {
    const emneknaggString = this.getAttribute('data-valgte-emneknagger') ?? ''
    return emneknaggString.split(';').filter(Boolean)
  }

  set valgteEmneknagger(val) {
    this.setAttribute('data-valgte-emneknagger', val.sort().join(';'))
  }

  get alleEmneknagger() {
    return JSON.parse(this.getAttribute('data-alle-emneknagger') ?? '')
  }

  set alleEmneknagger(value) {
    this.setAttribute('data-alle-emneknagger', JSON.stringify(value))
  }
}
